import TeacherService from "@/services/TeacherService";

function createState() {
  return {
    selecteFilter: [],
    loading: false,
    start: 0,
    end: 10,
    numberLessons: 10,
    totalLessons: 0,
    journal: {
      lessons: [],
      students: [],
    },
    teacherClass: {},
    currentSubject: {},
    studentGroupId: "",
    subjects: [],
    studentsList: [],
    evaluations: [],
    timeoutId: null,
    studentTimeout: null,
    currentStudentLessonId: "",
  };
}

const state = createState();

const getters = {
  getCurrentStudentLessonId(state) {
    return state.currentStudentLessonId;
  },
  selecteFilter(state) {
    return state.selecteFilter;
  },
  isLoading(state) {
    return state.loading;
  },
  getNumberLessons(state) {
    return state.numberLessons;
  },
  getStartLesson(state) {
    return state.start;
  },
  getEndLesson(state) {
    return state.end;
  },
  getLessonRange(state) {
    const parsedStartVal = parseInt(state.start);
    const startVal = isNaN(parsedStartVal) ? 0 : parsedStartVal;

    const parsedEndVal = parseInt(state.end);
    const endVal = isNaN(parsedEndVal) ? 10 : parsedEndVal;
    return {
      start: startVal,
      end: endVal,
      totalLessons: state.totalLessons,
      numberLessons: state.numberLessons,
    };
  },
  getCurrentSubject(state) {
    return state.currentSubject;
  },
  getTeacherClass(state) {
    return state.teacherClass;
  },
  getStudentGroupe(state) {
    return state.studentGroupId;
  },
  getJournal(state, getters) {
    const selecteFilter = getters.selecteFilter;
    if (selecteFilter.length === 0) {
      return state.journal;
    }
    const students = state.journal.students.map((student) => {
      return { ...student, evaluations: [] };
    });
    const journal = {
      lessons: [],
      students,
    };
    const allowTypes = selecteFilter.map((item) => item.value);
    return state.journal.lessons.reduce((journal, lesson, lessonIndex) => {
      if (allowTypes.includes(lesson.type)) {
        journal.lessons.push(lesson);
        journal.students.forEach((student, studentIndex) => {
          const stateStudent = state.journal.students[studentIndex];
          student.evaluations.push(stateStudent.evaluations[lessonIndex]);
        });
      }
      return journal;
    }, journal);
  },
  getSubjects: (state) => (currentSubjectId) => {
    const subjects = state.subjects.map((subject) => {
      const classes = subject.courses.map((course) => {
        return {
          id: course.id,
          grade: course.grade,
          title: `${course.grade} клас`,
          hasMessages: course.has_messages,
        };
      });
      classes.sort((a, b) => a.grade - b.grade);
      return {
        id: subject.id,
        lessonName: subject.name,
        active: currentSubjectId === subject.id,
        classes: classes,
        hasMessages: subject.has_messages,
      };
    });
    return subjects;
  },
  getStudentList(state) {
    const studentList = state.studentsList.reduce(
      (studentList, studentLesson) => {
        if (
          studentLesson.evaluation &&
          studentLesson.status !== 2 &&
          studentLesson.status !== 5 &&
          !studentLesson.has_messages
        ) {
          return studentList;
        }
        const rawDate = new Date(studentLesson.complete_date);
        let month = rawDate.getUTCMonth() + 1;
        let day = rawDate.getUTCDate();
        const year = rawDate.getUTCFullYear();
        month = month < 10 ? "0" + month : month;
        day = day < 10 ? "0" + day : day;
        const date = `${day}.${month}.${year}`;
        studentList.push({
          number: studentLesson.lesson.order,
          studentLessonId: studentLesson.id,
          firstName: studentLesson.student.first_name,
          lastName: studentLesson.student.last_name,
          lessonTitle: studentLesson.lesson.name,
          lessonOrder: studentLesson.lesson.order,
          hasMessages: studentLesson.has_messages ? "є повідомлення" : "-",
          hasUnreadMessages: studentLesson.has_messages,
          status: studentLesson.status,
          date,
        });
        return studentList;
      },
      []
    );
    studentList.sort((a, b) => a.lessonOrder - b.lessonOrder);

    return studentList;
  },
  getStudentsEvaluations(state) {
    return state.evaluations;
  },
  getStudentEvaluations: (state) => (id) => {
    const studentEvaluation = state.evaluations.find((evRes) => {
      return evRes.id === id;
    });
    if (!studentEvaluation) {
      return null;
    }
    return studentEvaluation;
  },
  getClassId(state) {
    return state?.teacherClass?.id || null;
  },
};

const actions = {
  async loadStudentEvaluation({ getters, commit }, { studentLessonId }) {
    const classId = getters.getClassId;
    const response = await TeacherService.loadStudentEvaluation(
      classId,
      studentLessonId
    );
    commit("setCurrentStudentLessonId", studentLessonId);
    return response.data;
  },
  calculateTheme({ commit }, payload) {
    const { id } = payload;
    return TeacherService.calculateTheme(id);
  },
  setChatRoom({ commit }, payload) {
    const { chatRoom, studentLessonId } = payload;
    commit("setChatRoom", { chatRoom, studentLessonId });
  },
  reset({ state }) {
    const emptyState = createState();
    Object.keys(state).forEach((key) => {
      state[key] = emptyState[key];
    });
  },
  async putToJournal({ commit, dispatch }, payload) {
    const { lessonId, totalEvaluation } = payload;
    await TeacherService.putToJournal(lessonId, totalEvaluation);
    await dispatch("loadJournal");
  },
  async backToRedo({ commit }, payload) {
    const { evaluationId, teacherComment } = payload;
    await TeacherService.backToRedo(evaluationId, teacherComment);
  },
  async loadTeacherSubjects({ commit }) {
    const response = await TeacherService.getLessons();
    const subjects = response.data.results || [];

    commit("setSubjects", subjects);
  },
  async loadStudentsLists({ commit }, payload) {
    const { teacherClass } = payload;
    const response = await TeacherService.getStudentsLists(teacherClass.id);
    const data = response.data[0] || {};
    const studentsList = data.results || [];

    const studentGroupe =
      (data?.results || []).find((course) => {
        return course?.student_group_id;
      })?.student_group_id ||
      data?.id ||
      "";
    commit("setStudentList", studentsList);
    commit("setEvaluations", studentsList);
    commit("setStudentGroupe", studentGroupe);
  },
  setCurrentTeacherRoom({ commit }, payload) {
    const { teacherClass } = payload;
    commit("setCurrentTeacherClass", teacherClass);
  },
  async addColumn({ commit }, payload) {
    await TeacherService.addColumn(payload);
  },
  async updateColumn({ commit }, payload) {
    const { id, data } = payload;
    await TeacherService.updateColumn(id, data);
  },
  async removeColumn({ commit }, payload) {
    const { lessonId } = payload;
    await TeacherService.removeColumn(lessonId);
  },
  async applyCustomRange({ getters, commit, dispatch }) {
    const { start, end, totalLessons } = getters.getLessonRange;
    const numberLessons = Math.max(end - start, 0);
    commit("setNumberLessons", numberLessons);
    commit(
      "setStart",
      Math.min(start, Math.max(totalLessons - numberLessons, 0))
    );
    commit("setEnd", Math.min(end, totalLessons));
    await dispatch("loadJournal");
  },
  async applyNextRange({ getters, commit, dispatch }) {
    const { start, end, numberLessons, totalLessons } = getters.getLessonRange;
    commit(
      "setStart",
      Math.min(start + numberLessons, Math.max(totalLessons - numberLessons, 0))
    );
    commit("setEnd", Math.min(end + numberLessons, totalLessons));
    await dispatch("loadJournal");
  },
  async applyPrevRange({ getters, commit, dispatch }) {
    const { start, end, numberLessons } = getters.getLessonRange;
    commit("setStart", Math.max(start - numberLessons, 0));
    commit("setEnd", Math.max(end - numberLessons, numberLessons));
    await dispatch("loadJournal");
  },
  async applyPageNumber({ getters, commit, dispatch }) {
    const { start, end, numberLessons, totalLessons } = getters.getLessonRange;
    commit(
      "setStart",
      Math.min(start, Math.max(totalLessons - numberLessons, 0))
    );
    commit("setEnd", Math.min(start + numberLessons, totalLessons));
    await dispatch("loadJournal");
  },
  async loadJournal({ state, commit, getters }) {
    commit("journalLoading", true);
    try {
      const { teacherClass } = state;
      let rawJournal = [];

      const start = getters.getStartLesson;
      const end = getters.getEndLesson;
      const selectFilter = getters.selecteFilter.map((item) => item.value);
      if (state.studentGroupId) {
        const response = await TeacherService.getJournal(
          teacherClass.id,
          state.studentGroupId,
          start,
          end,
          selectFilter
        );
        rawJournal = response.data || [];
      }
      commit("setTotalLessons", rawJournal?.totalLessons || 0);
      commit("setJournal", rawJournal);
    } catch (error) {
      console.log(`get error on load journal error: ${error}`);
    }
    commit("journalLoading", false);
  },
  async loadResults({ commit }, payload) {
    const { id } = payload;
    const response = await TeacherService.getStudentsResults(id);
    const evaluations = response.data.results || [];
    commit("setEvaluations", evaluations);
  },
  startUpdateSubject({ dispatch, commit }) {
    if (state.timeoutId) {
      clearTimeout(state.timeoutId);
    }
    const timeoutId = setTimeout(async function () {
      await dispatch("loadTeacherSubjects");
      dispatch("startUpdateSubject");
    }, 10 * 1000);
    commit("setTimeoutId", timeoutId);
  },
  async startUpdateStudentList({ dispatch, commit, state }, payload) {
    // if (state.studentTimeout) {
    //   clearTimeout(state.studentTimeout);
    // }
    // if (!payload) {
    //   return;
    // }
    const { teacherClass } = payload;
    // const timeoutId = setTimeout(async () => {
    //   await dispatch("loadStudentsLists", { teacherClass });
    //   dispatch("startUpdateStudentList", { teacherClass });
    // }, 10 * 1000);
    // commit("setStudentTimeoutId", timeoutId);
    await dispatch("loadStudentsLists", { teacherClass });
  },
};

const mutations = {
  setCurrentStudentLessonId(state, studentLessonId) {
    state.currentStudentLessonId = studentLessonId;
  },
  setSelecteFilter(state, val) {
    state.selecteFilter = val;
  },
  journalLoading(state, loading) {
    state.loading = loading;
  },
  setTimeoutId(state, timeoutId) {
    state.timeoutId = timeoutId;
  },
  setStudentTimeoutId(state, studentTimeout) {
    state.studentTimeout = studentTimeout;
  },
  setCurrentSubject(state, subject) {
    state.currentSubject = subject;
  },
  setSubjects(state, rawSubjects) {
    state.subjects = rawSubjects;
  },
  setStudentList(state, studentsList) {
    state.studentsList = studentsList;
  },
  setEvaluations(state, evaluations) {
    state.evaluations = evaluations;
  },
  setCurrentTeacherClass(state, teacherClass) {
    state.teacherClass = teacherClass;
  },
  setJournal(state, rawJournal) {
    state.journal = {
      ...state.journal,
      ...rawJournal,
    };
  },
  setTotalLessons(state, val) {
    state.totalLessons = val;
  },
  setNumberLessons(state, val) {
    state.numberLessons = val;
  },
  setStart(state, val) {
    state.start = val;
  },
  setEnd(state, val) {
    state.end = val;
  },
  setStudentGroupe(state, studentGroupId) {
    state.studentGroupId = studentGroupId;
  },
  setChatRoom(state, payload) {
    const { chatRoom, studentLessonId } = payload;
    const studentEvaluation = state.evaluations.find((evRes) => {
      return evRes.id === studentLessonId;
    });
    if (studentEvaluation) {
      studentEvaluation.chat_room = chatRoom;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
